// ================================================================================
// ResourceAllocation.jsx — Optimal Resource Allocation panel
// ================================================================================

import React, { useState } from "react";
import { ChevronUp, ChevronDown, Clock, Layers, DollarSign } from "lucide-react";
import { formatChannelName, formatResourceValue } from "./helpers";
import { ModernProgressBar } from "./SharedComponents";

const ResourceAllocation = ({ results, allRecs, campaignMetrics, weightedFit }) => {
    const [isAllocationsExpanded, setIsAllocationsExpanded] = useState(true);

    const isHybridROI = campaignMetrics?.gross_votes_per_hour > 0 && campaignMetrics?.gross_cost_per_vote > 0;

    const allocations = results?.campaign_optimization?.optimal_budget_allocation?.allocations ?? [];
    const topAllocation = [...allocations].sort((a, b) => b.allocation_percentage - a.allocation_percentage)[0];

    // Cost range for efficiency bars
    const financialRecs = allRecs.filter(r => r.mode !== 'time_only');
    const costs = financialRecs.map(r => r.average_cost_per_vote).filter(c => c > 0 && c !== Infinity);
    const minCost = costs.length > 0 ? Math.min(...costs) : 0;
    const maxCost = costs.length > 0 ? Math.max(...costs) : 0;
    const costRange = maxCost - minCost;

    const wardAnalysis = results?.ward_level_analysis
        ? Object.values(results.ward_level_analysis)[0]
        : null;

    return (
        <div className="panel p-6">
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-xl font-semibold text-white">Optimal Resource Allocation</h4>
                <button onClick={() => setIsAllocationsExpanded(!isAllocationsExpanded)} className="text-slate-400 hover:text-white transition-colors">
                    {isAllocationsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
            </div>

            {isAllocationsExpanded ? (
                <div className="space-y-4">
                    {allocations.map(alloc => {
                        const recData = allRecs.find(r => r.channel === alloc.channel);
                        const isTimeOnly = alloc.resource_mode === 'time_only';
                        const isHybrid = alloc.resource_mode === 'hybrid';

                        const realizedCPV = alloc.expected_votes > 0 && alloc.allocated_budget > 0
                            ? alloc.allocated_budget / alloc.expected_votes
                            : (recData?.average_cost_per_vote ?? maxCost);

                        let costEfficiencyPct = 0;
                        if (!isTimeOnly && realizedCPV !== Infinity) {
                            if (costRange > 0) {
                                costEfficiencyPct = ((maxCost - realizedCPV) / costRange) * 100;
                            } else {
                                costEfficiencyPct = 100;
                            }
                            costEfficiencyPct = Math.max(10, Math.min(100, costEfficiencyPct));
                        } else if (isTimeOnly) {
                            costEfficiencyPct = 95;
                        }

                        const demoFitPct = (
                            recData?.average_demographic_fit ??
                            (wardAnalysis?.ranked_channels?.find(c => c.channel === alloc.channel)?.demographic_fit ?? 0)
                        ) * 100;

                        return (
                            <div key={alloc.channel} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 relative overflow-hidden">
                                <div className={`absolute left-0 top-0 bottom-0 w-1 ${isTimeOnly ? 'bg-blue-500' : isHybrid ? 'bg-purple-500' : 'bg-emerald-500'}`}></div>

                                <div className="flex justify-between items-center mb-1 text-sm pl-2">
                                    <span className="font-medium text-slate-200 flex items-center gap-2">
                                        {formatChannelName(alloc.channel)}
                                        {isTimeOnly && <Clock size={14} className="text-blue-400" />}
                                        {isHybrid && <Layers size={14} className="text-purple-400" />}
                                    </span>
                                    <span className="font-mono text-slate-300">
                                        {formatResourceValue(alloc.allocated_budget, alloc.allocated_hours, alloc.resource_mode)}
                                        <span className="text-slate-500 ml-2">({(alloc.expected_votes ?? 0).toFixed(0)} votes)</span>
                                    </span>
                                </div>

                                <div className="w-full bg-slate-700 rounded-full h-2.5 mb-3 ml-2 pr-2">
                                    <div
                                        className={`${isTimeOnly ? 'bg-blue-500' : isHybrid ? 'bg-purple-500' : 'bg-accent-500'} h-2.5 rounded-full`}
                                        style={{ width: `${alloc.allocation_percentage ?? 0}%` }}
                                        title={`Allocation: ${alloc.allocation_percentage?.toFixed(1)}%`}
                                    ></div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 pl-2">
                                    <ModernProgressBar
                                        label={isTimeOnly ? "Time Efficiency" : "Resource Efficiency"}
                                        score={costEfficiencyPct}
                                        colorClass={isTimeOnly ? "bg-gradient-to-r from-blue-400 to-cyan-500" : "bg-gradient-to-r from-emerald-400 to-teal-500"}
                                    />
                                    <ModernProgressBar
                                        label="Demographic Fit"
                                        score={demoFitPct}
                                        colorClass="bg-gradient-to-r from-indigo-500 to-purple-500"
                                    />
                                </div>
                            </div>
                        );
                    })}
                    {allocations.length === 0 && (
                        <p className="text-slate-400 italic">No allocations generated. Check constraints.</p>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-700/30 p-4 rounded border border-slate-600/50 flex flex-col justify-center">
                        <span className="text-xs uppercase text-slate-400 font-semibold mb-1">Top Allocation</span>
                        <div className="text-lg font-bold text-white truncate" title={topAllocation?.channel}>{formatChannelName(topAllocation?.channel || 'None')}</div>
                        <div className="text-sm text-accent-400 font-mono">{(topAllocation?.allocation_percentage ?? 0).toFixed(1)}% Share</div>
                    </div>
                    <div className="bg-slate-700/30 p-4 rounded border border-slate-600/50 flex flex-col justify-center">
                        <span className="text-xs uppercase text-slate-400 font-semibold mb-1">Global Efficiency</span>
                        {isHybridROI ? (
                            <div className="text-xs text-white">Hybrid Optimized</div>
                        ) : campaignMetrics?.gross_votes_per_hour > 0 ? (
                            <div className="text-lg font-bold text-blue-400 font-mono">{campaignMetrics.gross_votes_per_hour.toFixed(1)} VPH</div>
                        ) : (
                            <div className="text-lg font-bold text-emerald-400 font-mono">£{(campaignMetrics?.gross_cost_per_vote ?? 0).toFixed(2)} CPV</div>
                        )}
                        <div className="text-xs text-slate-500">Aggregate Performance</div>
                    </div>
                    <div className="bg-slate-700/30 p-4 rounded border border-slate-600/50 flex flex-col justify-center">
                        <span className="text-xs uppercase text-slate-400 font-semibold mb-1">Weighted Fit</span>
                        <div className="text-lg font-bold text-indigo-400 font-mono">{weightedFit.toFixed(0)}%</div>
                        <ModernProgressBar score={weightedFit} colorClass="bg-gradient-to-r from-indigo-500 to-purple-500" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourceAllocation;
